import BaseExecutor from 'services/sports/default/base-executor';
import {groupObjectsByProp, getPropValues} from 'helpers/array-helper';
import Const from 'constants';
import {sortByProperty} from 'helpers/sorters';
import {getActiveGamesSinceTeamGameTimeline, getGamesIndexedByTeamName} from 'helpers/game-helper';
import Action from 'actions';
import {getMapOfLocked, canPlayerBeSetOnPosition} from 'components/organisms/late-swap/helpers';
import {round} from 'helpers/number-helper';

export default class BaseLineup extends BaseExecutor {
    getValidationRules({defaultValue = {}, ...rest}) {
        const {playersLock} = Action.getState();
        return {
            lockedPlayers: playersLock.getMergedLockedPlayerIds(),
        };
    }

    sumPlayersProperty({incomingValues: {players, prop}}) {
        let result = 0;

        if (prop === 'projected_ownership') {
            players.map((player) => {
                if ('projOwn' in player) {
                    result += Number(player.projOwn.getValue());
                }
            });
        } else {
            players.map((player) => (result += Number(player[prop])));
        }

        return result;
    }

    sumPlayersRoundedProperty({incomingValues: {players, prop, decimalPlacesCount = 0}}) {
        return players.reduce((propSum, player) => {
            const propValue = Number(player[prop]);

            return propSum + round(propValue, decimalPlacesCount);
        }, 0);
    }

    recalculateLineupsUsedPlayers({incomingValues: {lineups}}) {
        const usedPlayers = {};

        Object.values(lineups).map((lineup) => {
            lineup.players.map((player) => {
                const {playerId, position, positionInLineup} = player;
                const id = playerId + '__' + position;

                usedPlayers[id] ??= {
                    ...player,
                    usedLineupsCount: 0,
                    usedInLineupsAsFlex: 0,
                    usedInLineupsAsSuperFlex: 0,
                };

                usedPlayers[id].usedLineupsCount++;
                inArray(positionInLineup, ['FLEX', 'UTIL']) && usedPlayers[id].usedInLineupsAsFlex++;
                inArray(positionInLineup, ['S-FLEX', 'Super FLEX']) && usedPlayers[id].usedInLineupsAsSuperFlex++;
            });
        });

        return Object.values(usedPlayers);
    }

    recalculateLineupsUsedPlayers_ShowDown({incomingValues: {lineups}}) {
        const usedPlayers = {};

        Object.values(lineups).map((lineup) => {
            lineup.players.map((player) => {
                const id = player.playerId + '_' + Number(player.getIsCaptain());

                usedPlayers[id] ??= {...player, usedLineupsCount: 0};
                usedPlayers[id].usedLineupsCount++;
            });
        });

        return Object.values(usedPlayers);
    }

    prepareAllNotExcludedPlayersForExposureBoxDividedBySinglePosition({incomingValues: {players, sportConfig}}) {
        const result = [];
        players.map((sketchyPlayer) => {
            const positions = Array.isArray(sketchyPlayer.position) ? sketchyPlayer.position : [sketchyPlayer.position];

            positions.map((position) => {
                const player = cp(sketchyPlayer);

                player.usedLineupsCount = 0;
                player.usedInLineupsAsFlex = 0;
                player.usedInLineupsAsSuperFlex = 0;
                player.position = position;
                player.positionName = sportConfig.getPositionName(position, player.positionName);

                result.push(player);
            });
        });

        return result;
    }

    prepareAllNotExcludedPlayersForExposureBoxDividedByLineupRole({incomingValues: {players, sportConfig}}) {
        const {modesConfig} = Action.getState();
        const roles = modesConfig.all({asArr: true}).map((modeConfig) => modeConfig.getRole());

        const result = [];
        players.map((sketchyPlayer) => {
            roles.map((role) => {
                const player = cp(sketchyPlayer);

                player.usedLineupsCount = 0;
                player.lineupRole = role;

                result.push(player);
            });
        });

        return result;
    }

    prepareAllNotExcludedPlayersForExposureBox({incomingValues: {players, sportConfig}}) {
        if (sportConfig.isGameTypeClassic()) {
            return this.exec('prepareAllNotExcludedPlayersForExposureBoxDividedBySinglePosition', {
                players,
                sportConfig,
            });
        }

        if (sportConfig.isGameTypeShowDownOrSingleGame()) {
            return this.exec('prepareAllNotExcludedPlayersForExposureBoxDividedByLineupRole', {
                players,
                sportConfig,
            });
        }

        return players.map((sketchyPlayer) => {
            const player = cp(sketchyPlayer);

            player.usedLineupsCount = 0;

            return player;
        });
    }

    combineOptimizedAndNotExcludedPlayersByPositionName({incomingValues: {optimizedPlayers, notExcludedPlayers}}) {
        const createIndex = (player) => player.getId() + '-' + player.positionName;

        const indexedPlayers = {};
        optimizedPlayers.map((player) => (indexedPlayers[createIndex(player)] = player));

        notExcludedPlayers.map((player) => {
            const index = createIndex(player);

            indexedPlayers[index] ??= player;
        });

        return Object.values(indexedPlayers);
    }

    combineOptimizedAndNotExcludedPlayersByLineupRole({incomingValues: {optimizedPlayers, notExcludedPlayers}}) {
        const createIndex = (player) => player.getId() + '-' + player.lineupRole;

        const indexedPlayers = {};
        optimizedPlayers.map((player) => (indexedPlayers[createIndex(player)] = player));

        notExcludedPlayers.map((player) => {
            const index = createIndex(player);

            indexedPlayers[index] ??= player;
        });

        return Object.values(indexedPlayers);
    }

    combineOptimizedAndNotExcludedPlayers({incomingValues: {optimizedPlayers, notExcludedPlayers, sportConfig}}) {
        if (sportConfig.isGameTypeClassic()) {
            return this.exec('combineOptimizedAndNotExcludedPlayersByPositionName', {
                optimizedPlayers,
                notExcludedPlayers,
            });
        }

        if (sportConfig.isGameTypeShowDownOrSingleGame()) {
            return this.exec('combineOptimizedAndNotExcludedPlayersByLineupRole', {
                optimizedPlayers,
                notExcludedPlayers,
            });
        }

        const optimizedPlayerIds = arrayUnique(optimizedPlayers.map((player) => player.getId()));
        const result = cp(optimizedPlayers); // do not index optimized players to avoid losing players on CPT and FLEX

        notExcludedPlayers.map((player) => {
            if (!inArray(player.getId(), optimizedPlayerIds)) {
                result.push(player);
                optimizedPlayerIds.push(player.getId());
            }
        });

        return result;
    }

    forcePlayerToFlexPosition({incomingValues}) {
        let {
            lineupPlayers,
            flexPositionNames,
            isPlayerSubstitutable = (toPlayer, fromPlayer, gamesIndexedByTeamName) => true,
            getSubstitutablePositionNames = (player) => [player.positionName],
            changePositionIfNeeded = (player) => {},
            groupPlayersByPosition = (players) => groupObjectsByProp(players, 'positionName'),
        } = incomingValues;

        if (isEmpty(lineupPlayers)) {
            return [];
        }

        const lineupPositions = groupObjectsByProp(lineupPlayers, 'positionInLineup');
        const playerPositions = groupPlayersByPosition(lineupPlayers);
        flexPositionNames = flexPositionNames || [Const.POSITION_FLEX];

        const flexPlayers = [];
        flexPositionNames.map((flexPositionName) => {
            lineupPositions[flexPositionName].map((player) => flexPlayers.push(player));
        });

        const flexPlayersId = getPropValues(flexPlayers, 'playerId');
        flexPlayers.sort(sortByProperty('salary'));

        const gamesIndexedByTeamName = getGamesIndexedByTeamName();

        const replacements = [];
        const usedPlayerIds = [];
        flexPlayers.map((flexPlayer) => {
            const activeGames = getActiveGamesSinceTeamGameTimeline(flexPlayer.team);

            if (isEmpty(activeGames)) {
                return;
            }

            activeGames.sort(sortByProperty('gameTimestamp', 'desc'));

            const playersStack = [];
            Object.values(activeGames).map((game) => {
                const players = [];
                getSubstitutablePositionNames(flexPlayer).map((position) => {
                    (playerPositions[position] ?? [])
                        .filter((player) => {
                            return game.teams.includes(player.team)
                                && isPlayerSubstitutable(player, flexPlayer, gamesIndexedByTeamName);
                        })
                        .map((player) => players.push(player));
                });

                if (!isEmpty(players)) {
                    players.sort(sortByProperty('salary', 'desc'));
                    players.map((player) => playersStack.push(player));
                }
            });

            if (isEmpty(playersStack)) {
                return;
            }

            for (const player of playersStack) {
                if (String(flexPlayer.playerId) === String(player.playerId)) {
                    break;
                }

                if (inArray(player.playerId, flexPlayersId) || inArray(player.playerId, usedPlayerIds)) {
                    continue;
                }

                const newFlexPlayer = {...player};
                const positionInLineup = flexPlayer.positionInLineup;
                const positionIdInLineup = flexPlayer.positionIdInLineup;

                flexPlayer.positionInLineup = newFlexPlayer.positionInLineup;
                flexPlayer.positionIdInLineup = newFlexPlayer.positionIdInLineup;
                changePositionIfNeeded(flexPlayer);

                newFlexPlayer.positionInLineup = positionInLineup;
                newFlexPlayer.positionIdInLineup = positionIdInLineup;
                changePositionIfNeeded(newFlexPlayer);

                replacements.push({
                    from: flexPlayer,
                    to: newFlexPlayer,
                });
                usedPlayerIds.push(newFlexPlayer.playerId);
                // break iteration because players array was sorted by salary.
                break;
            }
        });

        if (isEmpty(replacements)) {
            return [...lineupPlayers];
        }

        let result = [];
        replacements.map((replacement) => {
            result = [];
            lineupPlayers.map((player) => {
                if (String(player.playerId) === String(replacement.from.playerId)) {
                    result.push(replacement.to);
                } else if (String(player.playerId) === String(replacement.to.playerId)) {
                    result.push(replacement.from);
                } else {
                    result.push(player);
                }
            });
            lineupPlayers = [...result];
        });

        return [...result];
    }

    getFunctionsToSetPlayersByColumns({incomingValues: {lineupId}}) {
        const {
            sportConfig,
            lineupsData: {rawLineups},
            lateSwap: {csvLineups},
            playersData: {games},
        } = Action.getState();

        const canPlayerBeSetOnPositionFunctions = [];

        if (!sportConfig.isModeLateswap()) {
            return {canPlayerBeSetOnPositionFunctions};
        }

        const simplexLineup = rawLineups.getLineup(lineupId);
        const lateswapCsvLineup = csvLineups.getOneByEntryId(simplexLineup.getEntryId());
        const mapOfLocked = getMapOfLocked(
            sportConfig.getConfigProp('positionsOrder', {}),
            lateswapCsvLineup,
            function (player) {
                // https://abnk.atlassian.net/browse/DFSA-265
                return csvLineups.isLoadingStatusInProgress() || games.isPlayerGameLocked(player);
            },
        );

        canPlayerBeSetOnPositionFunctions.push((player, position) =>
            canPlayerBeSetOnPosition(mapOfLocked, player, position),
        );

        return {canPlayerBeSetOnPositionFunctions};
    }
}
